"use client";

import { useRouter } from "next/navigation";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { TimeZoneChoice } from "@/domain/market/timeZones";
import { api } from "@/lib/useApi";
import { createTranslator, DEFAULT_LOCALE, makeI18n, type I18n, type Locale } from "./translate";

/** The language and the time zone the user chose ("local": the device's), and the change of the zone. */
interface Shown {
  locale: Locale;
  zone: TimeZoneChoice;
  choose: (zone: TimeZoneChoice) => void;
}
const ShownContext = createContext<Shown>({ locale: DEFAULT_LOCALE, zone: "local", choose: () => undefined });

/**
 * The user's language and time zone for the client components. A zone chosen here (the chart's clock) shows at once and
 * is saved in the user's preferences; the saved one comes back with the page (a failed save: the one before).
 */
export function I18nProvider({ locale, timeZone, children }: { locale: Locale; timeZone: TimeZoneChoice; children: ReactNode }) {
  const router = useRouter();
  const [draft, setDraft] = useState<TimeZoneChoice | null>(null);
  const [seen, setSeen] = useState(timeZone);
  if (seen !== timeZone) {
    setSeen(timeZone);
    setDraft(null);
  }
  const zone = draft ?? timeZone;
  const value = useMemo<Shown>(() => {
    const choose = (next: TimeZoneChoice) => {
      setDraft(next);
      void api("/api/settings/preferences", { method: "PUT", json: { timeZone: next } }).then(() => router.refresh(), () => setDraft(null));
    };
    return { locale, zone, choose };
  }, [locale, zone, router]);
  return <ShownContext.Provider value={value}>{children}</ShownContext.Provider>;
}

/** Translator + locale-aware formatters for client components, their times in the user's time zone. */
export function useI18n(): I18n {
  const { locale, zone } = useContext(ShownContext);
  return useMemo(() => makeI18n(createTranslator(locale, zone === "local" ? undefined : zone)), [locale, zone]);
}

/** The time zone the user chose ("local": the device's) and its change. */
export function useTimeZone(): Pick<Shown, "zone" | "choose"> {
  const { zone, choose } = useContext(ShownContext);
  return { zone, choose };
}
